import i18next from 'i18next';
import React, { memo, useState, useEffect, ReactElement } from 'react';
import { Accordion, Badge, Button, Card, Col, Modal, ProgressBar, Row, useAccordionButton } from 'react-bootstrap';
import { titleCase } from '../../../util/urls';
import { formatDate } from '../../../util/date-utils';
import MediaPlayer from '../../../components/MediaPlayer';
import { ExerciseAnswer, ExerciseStatus, PromQuestionType, TrainingAnswer, TrainingStatus } from '../../../../../shared/utils/enums';
import { VideoFeedbackDto } from '../../../../../shared/dtos/trainingHistory.dto';
import {
  AnalysisExerciseDto,
  AnalysisHistoryDto,
  AnalysisPromAnswerDto,
  AnalysisWeekDto,
  TrainingPlanStatisticDto,
  WeekEnduranceTrainingDto,
  WeekTrainingDto,
} from '../../../../../shared/dtos/therapyStatistic.dto';
import { EnduranceTrainingHistoryDto } from '../../../../../shared/dtos/enduranceTrainingHistory.dto';

const ABORT_ANSWERS: (TrainingAnswer | ExerciseAnswer)[] = [TrainingAnswer.NOT_FIT, TrainingAnswer.NO_TIME, TrainingAnswer.NO_ANSWER];

const variantByStatus: Record<string, string> = {
  [TrainingStatus.DONE]: 'badge-bg-info',
  [TrainingStatus.SKIPPED]: 'badge-bg-info',
  [TrainingStatus.DONE_WITH_SKIPS]: 'badge-bg-info',
  [TrainingStatus.ABORTED]: 'badge-bg-danger',
};

const rowKeyOf = (week: AnalysisWeekDto, trainingName: string): string =>
  `${week.year}-W${week.weekNumber}|${trainingName}`;

const enduranceRowKeyOf = (week: AnalysisWeekDto, trainingName: string): string =>
  `${week.year}-W${week.weekNumber}|endurance|${trainingName}`;

const isAbortAnswer = (answer: TrainingAnswer | ExerciseAnswer): boolean => ABORT_ANSWERS.includes(answer);

const VideoFeedbackModalBody = ({ feedbacks }: { feedbacks: VideoFeedbackDto[] }): ReactElement => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const selected: VideoFeedbackDto = feedbacks[selectedIndex];
  const hasList: boolean = feedbacks.length > 1;

  return (
    <Row>
      {hasList && (
        <Col md={3} className="border-end" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {feedbacks.map((feedback: VideoFeedbackDto, index: number) => (
            <div
              key={index}
              className={`d-flex align-items-center p-2 rounded mb-1 ${index === selectedIndex ? 'bg-primary bg-opacity-10 border border-primary' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => setSelectedIndex(index)}
            >
              <video
                src={feedback.videoUrl}
                className="rounded me-2"
                style={{ width: '48px', height: '48px', objectFit: 'cover' }}
                muted
                preload="metadata"
              />
              <span className="fw-bold small">Set {feedback.setNumber}</span>
            </div>
          ))}
        </Col>
      )}
      <Col md={hasList ? 9 : 12}>
        <MediaPlayer
          key={selected.videoUrl}
          src={selected.videoUrl}
          height={400}
          cornerRadius={6}
          autoPlay={false}
          muted={false}
          loop={false}
        />
      </Col>
    </Row>
  );
};

const TrainingPlanAccordion = ({ statistic }: { statistic?: TrainingPlanStatisticDto }): ReactElement => {
  const [activeIds, setActiveIds] = useState<string[]>([]);
  const [selectedVideoFeedbacks, setSelectedVideoFeedbacks] = useState<VideoFeedbackDto[] | null>(null);

  const weeks: AnalysisWeekDto[] = statistic?.weeks ?? [];

  const toggleActive = (id: string): void => {
    setActiveIds((previous: string[]) =>
      previous.includes(id) ? previous.filter((x: string) => x !== id) : [...previous, id],
    );
  };

  useEffect(() => {
    const firstWeek: AnalysisWeekDto | undefined = weeks[0];
    const firstTraining: WeekTrainingDto | undefined = firstWeek?.trainings[0];

    if (firstWeek && firstTraining) {
      setActiveIds([rowKeyOf(firstWeek, firstTraining.trainingName)]);
    }
  }, [statistic]);

  const renderPromAnswer = (prom: AnalysisPromAnswerDto, index: number): ReactElement => {
    const questionTitle: string = prom.question?.title ?? '';

    if (prom.questionType === PromQuestionType.RANGE) {
      const score: number = prom.value ?? 0;

      return (
        <div key={'prom_' + index}>
          <p className="mb-0">
            {titleCase(questionTitle)}
            <span className="float-end pe-4">{score}/10</span>
          </p>
          <ProgressBar className="mb-1 h-8" now={Math.max(0, Math.min(score, 10)) * 10} />
        </div>
      );
    }

    const displayValue: string = prom.value !== null
      ? String(prom.value)
      : (prom.textValue ?? i18next.t('answerAndActivity.promNoAnswer'));

    return (
      <div key={'prom_' + index}>
        <p className="mb-1">
          {titleCase(questionTitle)}
          <span className="float-end pe-4 fw-bold">{displayValue}</span>
        </p>
      </div>
    );
  };

  const renderExercise = (exercise: AnalysisExerciseDto, index: number): ReactElement => {
    const isDone: boolean = exercise.status === ExerciseStatus.DONE;
    const hasVideoFeedback: boolean = exercise.videoFeedbacks.length > 0;
    const isAborted: boolean = isAbortAnswer(exercise.answer);

    return (
      <Col md={12} key={'ex_' + index}>
        <i
          className={`fas ${isDone ? 'fa-check-circle text-success' : 'fa-times-circle text-danger'}`}
          aria-hidden="true"
        />
        &nbsp; {exercise.name}
        {hasVideoFeedback && (
          <Button
            variant="link"
            size="sm"
            className="p-0 ms-2 primary-text-color"
            onClick={() => setSelectedVideoFeedbacks(exercise.videoFeedbacks)}
          >
            <i className="fas fa-video" aria-hidden="true" />
          </Button>
        )}

        {(exercise.comment || isAborted) && (
          <div className="p-2 rounded-2" style={{ background: '#f7f7f7', border: '1px solid #dfdfdf' }}>
            {isAborted && <span className="fw-bold">{i18next.t('answerAndActivity.abortedText.' + exercise.answer)}</span>}
            {exercise.comment && (
              <p
                className="mb-0"
                style={{ fontSize: '14px', lineHeight: '21px', color: '#161616' }}
                dangerouslySetInnerHTML={{ __html: exercise.comment }}
              />
            )}
          </div>
        )}
      </Col>
    );
  };

  function TrainingStatusHeaderTrigger({
    rowKey,
    training,
    isCurrent,
  }: {
    rowKey: string;
    training: { trainingName: string; completedTraining: number; targetPerWeek: number };
    isCurrent: boolean;
  }): ReactElement {
    const decoratedOnClick = useAccordionButton(rowKey, () => toggleActive(rowKey));
    const doneAll: boolean = training.completedTraining === training.targetPerWeek;
    const expanded: boolean = activeIds.includes(rowKey);
    const statusClass: string = doneAll ? 'text-success' : isCurrent ? '' : 'text-danger';

    return (
      <div
        className="d-flex align-items-center justify-content-between p-3 fw-bold"
        role="button"
        tabIndex={0}
        onClick={decoratedOnClick}
      >
        <span className={statusClass}>
          {doneAll ? (
            <i className="fas fa-check-circle me-2" aria-hidden="true" />
          ) : isCurrent ? null : (
            <i className="fas fa-times-circle me-2" aria-hidden="true" />
          )}
          {training.trainingName}
        </span>
        <span className="d-flex align-items-center gap-3">
          <span className={statusClass}>
            {training.completedTraining}/{training.targetPerWeek}
          </span>
          <i className={`fas fa-angle-${expanded ? 'up' : 'down'} text-muted`} aria-hidden="true" />
        </span>
      </div>
    );
  }

  const renderHistoryCard = (
    history: AnalysisHistoryDto,
    index: number,
    historyList: AnalysisHistoryDto[],
  ): ReactElement => {
    const isAbortedAnswer: boolean = isAbortAnswer(history.answer);

    return (
      <Row
        className={historyList.length - 1 !== index ? 'border-color border-bottom mb-3 p-3' : 'mb-3 p-3'}
        key={'tp_content_' + index}
      >
        <Col md={6} xs={6}>
          <p className="fw-bold">{formatDate(history.startDate)}</p>
        </Col>

        <Col md={6} xs={6}>
          <Badge className={'float-end badge-status align-top me-3 ' + (variantByStatus[history.status] ?? 'badge-bg-info')}>
            {i18next.t('answerAndActivity.exerciseStatus.' + history.status)}
          </Badge>
        </Col>

        <Col md={7}>
          {history.generalFeedback && (
            <>
              <p className="fw-bold mb-0">{i18next.t('answerAndActivity.generalFeedback')}</p>
              <div className="p-2 rounded-2" style={{ background: '#f7f7f7', border: '1px solid #dfdfdf' }}>
                <p dangerouslySetInnerHTML={{ __html: history.generalFeedback }} />
              </div>
            </>
          )}

          {isAbortedAnswer && (
            <>
              <p className="fw-bold mb-0">{i18next.t('answerAndActivity.abortedLabel')}</p>
              <div className="p-2 rounded-2" style={{ background: '#f7f7f7', border: '1px solid #dfdfdf' }}>
                <span className="fw-bold">{i18next.t('answerAndActivity.abortedText.' + history.answer)}</span>
                {history.comment && <p dangerouslySetInnerHTML={{ __html: history.comment }} />}
              </div>
            </>
          )}

          {history.videoFeedbackDeclineReason && (
            <>
              <p className="fw-bold mb-0">{i18next.t('answerAndActivity.videoFeedbackDeclineReason')}</p>
              <div className="p-2 rounded-2" style={{ background: '#f7f7f7', border: '1px solid #dfdfdf' }}>
                <p>
                  {history.videoFeedbackDeclineReason === TrainingAnswer.NO_ANSWER
                    ? i18next.t('answerAndActivity.videoFeedbackDeclineNoAnswer')
                    : history.videoFeedbackDeclineReason}
                </p>
              </div>
            </>
          )}

          <p className="fw-bold mt-2 mb-1">{i18next.t('answerAndActivity.exerciseLabel')}</p>

          {history.comment && !isAbortedAnswer && (
            <div className="p-2 rounded-2" style={{ background: '#f7f7f7', border: '1px solid #dfdfdf' }}>
              <p dangerouslySetInnerHTML={{ __html: history.comment }} />
            </div>
          )}

          <Row className="ps-2">{history.exercises.map((exercise, i) => renderExercise(exercise, i))}</Row>
        </Col>

        <Col md={5} className="pt-2">
          {history.promAnswers.map((prom, j) => renderPromAnswer(prom, j))}
        </Col>
      </Row>
    );
  };

  const renderTrainingHeader = (week: AnalysisWeekDto, training: WeekTrainingDto, index: number): ReactElement => {
    const rowKey: string = rowKeyOf(week, training.trainingName);

    const expanded: boolean = activeIds.includes(rowKey);

    return (
      <Card
        className={`mb-2 overflow-hidden ${expanded ? 'shadow-sm' : ''}`}
        style={{ borderRadius: 12, border: `1px solid ${expanded ? '#cfd8dc' : '#eceff1'}` }}
        key={'training_header_' + index}
      >
        <Accordion activeKey={expanded ? rowKey : undefined}>
          <Card.Header
            className={`p-0 ${expanded ? 'border-bottom' : 'border-0'}`}
            style={{ cursor: 'pointer', background: expanded ? '#f2f5f7' : '#fff' }}
          >
            <TrainingStatusHeaderTrigger rowKey={rowKey} training={training} isCurrent={week.isCurrent} />
          </Card.Header>

          <Accordion.Collapse eventKey={rowKey}>
            <Card.Body className="pt-3">
              {training.history.length > 0
                ? training.history.map((history, k) => renderHistoryCard(history, k, training.history))
                : <p className="text-muted mb-0">{i18next.t('answerAndActivity.noTrainingPlanActivity')}</p>}
            </Card.Body>
          </Accordion.Collapse>
        </Accordion>
      </Card>
    );
  };

  const renderEnduranceHistoryCard = (
    history: EnduranceTrainingHistoryDto,
    index: number,
    historyList: EnduranceTrainingHistoryDto[],
    typeName: string,
  ): ReactElement => {
    return (
      <Row
        className={historyList.length - 1 !== index ? 'border-color border-bottom mb-3 p-3' : 'mb-3 p-3'}
        key={'etp_content_' + index}
      >
        <Col md={6} xs={6}>
          <p className="fw-bold mb-0">{formatDate(history.completedAt)}</p>
        </Col>

        <Col md={6} xs={6}>
          <Badge className={'float-end badge-status align-top me-3 ' + (variantByStatus[history.status] ?? 'badge-bg-info')}>
            {i18next.t('answerAndActivity.exerciseStatus.' + history.status)}
          </Badge>
        </Col>

        <Col md={7}>
          <Row>
            <Col xs={7}>
              <p className="fw-bold mt-2 mb-1">{i18next.t('answerAndActivity.enduranceTrainingTypeLabel')}</p>
              <p className="ps-2 mb-0">{typeName}</p>
            </Col>

            <Col xs={5}>
              <p className="fw-bold mt-2 mb-1">{i18next.t('answerAndActivity.trainingMinutes')}</p>
              <p className="ps-2 mb-0">{history.trainingMinutes}</p>
            </Col>
          </Row>

          {history.generalFeedback && (
            <>
              <p className="fw-bold mt-3 mb-1">{i18next.t('answerAndActivity.generalFeedback')}</p>
              <div className="p-2 rounded-2" style={{ background: '#f7f7f7', border: '1px solid #dfdfdf' }}>
                <p className="mb-0">{history.generalFeedback}</p>
              </div>
            </>
          )}
        </Col>

        {history.promAnswers.length > 0 && (
          <Col md={5} className="pt-2 ms-auto">
            {history.promAnswers.map((prom, j) => renderPromAnswer(prom, j))}
          </Col>
        )}
      </Row>
    );
  };

  const renderEnduranceTrainingHeader = (
    week: AnalysisWeekDto,
    enduranceTraining: WeekEnduranceTrainingDto,
    index: number,
  ): ReactElement => {
    const rowKey: string = enduranceRowKeyOf(week, enduranceTraining.trainingName);

    const expanded: boolean = activeIds.includes(rowKey);

    const typeName: string = i18next.language === 'en'
      ? enduranceTraining.enduranceTrainingType.enName
      : enduranceTraining.enduranceTrainingType.deName;

    return (
      <Card
        className={`mb-2 overflow-hidden ${expanded ? 'shadow-sm' : ''}`}
        style={{ borderRadius: 12, border: `1px solid ${expanded ? '#cfd8dc' : '#eceff1'}` }}
        key={'endurance_training_header_' + index}
      >
        <Accordion activeKey={expanded ? rowKey : undefined}>
          <Card.Header
            className={`p-0 ${expanded ? 'border-bottom' : 'border-0'}`}
            style={{ cursor: 'pointer', background: expanded ? '#f2f5f7' : '#fff' }}
          >
            <TrainingStatusHeaderTrigger rowKey={rowKey} training={enduranceTraining} isCurrent={week.isCurrent} />
          </Card.Header>

          <Accordion.Collapse eventKey={rowKey}>
            <Card.Body className="pt-3">
              {enduranceTraining.history.length > 0
                ? enduranceTraining.history.map((history, k) =>
                  renderEnduranceHistoryCard(history, k, enduranceTraining.history, typeName),
                )
                : <p className="text-muted mb-0">{i18next.t('answerAndActivity.noTrainingPlanActivity')}</p>}
            </Card.Body>
          </Accordion.Collapse>
        </Accordion>
      </Card>
    );
  };

  const renderWeek = (week: AnalysisWeekDto, index: number): ReactElement => {
    return (
      <div key={'tp_card_' + week.year + '-W' + week.weekNumber + '_' + index} className="mb-3">
        <div className="d-flex align-items-center gap-2 mt-4 mb-2 px-1">
          <span className="fw-bold text-uppercase" style={{ letterSpacing: '0.5px', color: '#234a68' }}>
            {i18next.t('answerAndActivity.weekText')} {week.weekNumber}
          </span>
          {week.isCurrent && <Badge className="badge-bg-info">{i18next.t('answerAndActivity.currentText')}</Badge>}
        </div>
        {[
          ...week.trainings.map((training, index) => ({
            order: training.order,
            element: renderTrainingHeader(week, training, index),
          })),
          ...week.enduranceTrainings.map((enduranceTraining, index) => ({
            order: enduranceTraining.order,
            element: renderEnduranceTrainingHeader(week, enduranceTraining, index),
          })),
        ]
          .sort((a, b) => a.order - b.order)
          .map((entry) => entry.element)}
      </div>
    );
  };

  return (
    <div style={{ fontFamily: 'Montserrat' }}>
      {weeks.length === 0 ? (
        <Card className="mb-2 border shadow-sm rounded-3 bg-white">
          <Card.Body>{i18next.t('answerAndActivity.noTrainingPlanActivity')}</Card.Body>
        </Card>
      ) : (
        <div>{weeks.map((week, index) => renderWeek(week, index))}</div>
      )}

      <Modal show={selectedVideoFeedbacks !== null} onHide={() => setSelectedVideoFeedbacks(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{i18next.t('answerAndActivity.videoFeedbacks')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{selectedVideoFeedbacks && <VideoFeedbackModalBody feedbacks={selectedVideoFeedbacks} />}</Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setSelectedVideoFeedbacks(null)}>
            {i18next.t('generic.close')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default memo(TrainingPlanAccordion);
